'use client';

import '../uiPrimitives/base/layersStyle.css';
import '../uiPrimitives/base/base.css';
import { translateInline } from '@/globals/translate/translateInline';
import { siteTitle } from '@/globals/translate/siteMetaTranslations';
import { languageCode } from '@/globals/config/localization.config';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { rijksFont } from '@/uiPrimitives/font/fontFace';
import {
	headerAndPageGrid,
	rootGrid,
} from '@/uiPrimitives/layout/pageGrid/rootGrid.css';
import { focusMain } from '@/uiPrimitives/base/focus.css';
import { pageGrid } from '@/uiPrimitives/layout/pageGrid/pageGrid';
import { position } from '@/uiPrimitives/layout/position';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { box } from '@/uiPrimitives/layout/box';
import {
	pageHeaderGrid,
	pageHeaderLogoSlot,
	pageHeaderRijksSlot,
} from '@/sections/pageHeader/PageHeader.css';
import { overflowClip } from '@/uiPrimitives/utility/overflow.css';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { IconRijkslogo } from '@/uiIcons/IconRijkslogo';
import { SiteLogo } from '@/sections/pageHeader/SiteLogo';
import { siteLogo } from '@/sections/pageHeader/SiteLogo.css';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { footerContainer } from '@/sections/pageFooter/PageFooter.css';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { H1TextBlock } from '@/uiComposites/typography/headings';
import { icon } from '@/uiPrimitives/media/icon';
import { warningIcon } from '@/uiIcons/warningIcon.css';
import { stack } from '@/uiPrimitives/layout/stack';

// export of Metadata does not work in global error pages

export default function GlobalError() {
	return (
		<html lang={languageCode} className={classes(rijksFont.className)}>
			<head>
				<title>{`${translateInline({ en: 'Website unavailable', nl: 'Website niet beschikbaar' })} | ${siteTitle}`}</title>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta name="robots" content="noindex,nofollow" />
				<meta charSet="utf-8" />
				<meta
					name="description"
					content={`${translateInline({ en: 'The website is currently unavailable', nl: 'De website is momenteel niet beschikbaar' })}`}
				/>
			</head>
			<body>
				<div className={rootGrid}>
					<div className={headerAndPageGrid}>
						<div
							className={classes(
								pageGrid({}),
								position({ position: 'relative' }),
								zIndex({ zIndex: 'brandBar' }),
								box({ backgroundColor: 'negative' }),
							)}
						>
							<div className={classes(pageHeaderGrid)}>
								<div className={classes(pageHeaderRijksSlot)}>
									<div
										className={classes(
											overflowClip,
											occupy({
												blockSize: {
													max640px: '8 | betweenCards',
												},
												alignItems: 'end',
											}),
										)}
									>
										<IconRijkslogo
											aria-label={translateInline({
												en: 'Logo Dutch central government',
												nl: 'Logo Rijksoverheid',
											})}
											className={box({
												inlineSize: {
													all: '7 | betweenH3Sections',
													max640px: '6 | betweenH4Sections',
												},
												blockSize: 'auto',
											})}
										/>
									</div>
								</div>
								<div className={classes(pageHeaderLogoSlot)}>
									<SiteLogo className={siteLogo} />
								</div>
							</div>
						</div>
						<div
							className={classes(
								pageGrid({}),
								box({
									blockSize: '8 | betweenCards',
									backgroundColor: 'positiveBlue',
								}),
							)}
						></div>
						<main
							className={classes(
								pageGrid({ alignItems: 'start', columnCount: '8-of-12' }),
								position({ position: 'relative' }),
								box({ paddingBlock: { all: '8 | betweenCards' } }),
								focusMain,
							)}
							tabIndex={-1}
						>
							<div className={classes(stack({ rowGap: '8 | betweenCards' }))}>
								<div
									className={classes(
										shelf({
											columnGap: '3 | h1',
										}),
									)}
								>
									<i
										className={classes(
											icon({
												icon: warningIcon,
												fill: 'positiveBlue',
												size: '9 | asideBlockMargin',
											}),
										)}
									/>
									<div className={classes(stack({ rowGap: '3 | h1' }))}>
										<H1TextBlock>
											{translateInline({
												en: 'Oops, something went wrong.',
												nl: 'Oeps... Er is iets misgegaan,',
											})}
										</H1TextBlock>
										<H1TextBlock>
											{translateInline({
												en: 'But not because of something you did.',
												nl: 'maar het ligt niet aan u',
											})}
										</H1TextBlock>
									</div>
								</div>
								<div
									className={classes(
										stack({ rowGap: '6 | betweenH4Sections' }),
									)}
								>
									<p className={classes(textBlock({}))}>
										{translateInline({
											en: 'Our website cannot be loaded. We are working hard to fix this.',
											nl: 'Onze site kan niet geladen worden. Wij zijn druk bezig om dit te herstellen.',
										})}
									</p>
									<p
										className={classes(
											textBlock({ fontWeight: 'bold', capSize: '1 | h3' }),
										)}
									>
										{translateInline({
											en: 'Please try again later.',
											nl: 'Probeer het later nog eens.',
										})}
									</p>
								</div>
							</div>
						</main>
					</div>

					<footer
						className={classes(
							box({
								backgroundColor: 'positiveProjectBrand',
								paddingBlock: '7 | betweenH3Sections',
							}),
							pageGrid({}),
							textStyle({ fontWeight: 'bold' }),
							footerContainer,
						)}
					></footer>
				</div>
			</body>
		</html>
	);
}
